import React, { useEffect, useRef, useState } from 'react';
import './policies.scss';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  copyTextToClipboard,
  getPaginationOpts,
  getResponseError,
  responseLogWithNotification,
  useFilterForm,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getPoliciesData } from '../reducer';
import Loader from '../../../components/base/Loader/Loader';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import Pagination from '../../../components/base/Pagination/Pagination';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import PoliciesDTO from '../../../dto/PoliciesDTO';
import PoliciesForm from './PoliciesForm';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { showGlobalConfirm } from '../../System/actions';
import RangeDatePicker from '../../../components/fields/RangeDatePicker/RangeDatePicker';
import FilterForm from './FilterForm';
import { policiesAdditionalColumns, policiesColumns } from './policiesColumns';
import {
  deletePolicies, loadPoliciesById, loadPoliciesByIdClear, loadPoliciesList,
} from '../actions';
import { POLICIES_MODULE } from '../../../configs/moduleNames';

export default function PoliciesPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const { updatePeriod } = useSelector(getSystemData);

  const store = useStore();
  const {
    policiesListMeta,
    loadingPoliciesList,
    policiesList,
    loadingCurrentPolicies,
    currentPolicies,
    loadingCreatePolicies,
    loadingEditPoliciesById,
    loadingDeletePolicies,
  } = useSelector(getPoliciesData);
  const { formatMessage } = useIntl();

  const loadPolicies = id => {
    if (id) {
      dispatch(loadPoliciesById(id))
        .then(() => setShowForm(true))
        .catch(response => errorNotification('Загрузка политик', getResponseError(response)));
    }
  };

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[POLICIES_MODULE].policiesListMeta, opts); // костыль внедрен из за потери контекста в updatePeriod. Он сохранял первую функцию
    const requestData = {
      ...paginationOpts,
      filter: getClearValues(),
    };
    const p1 = dispatch(loadPoliciesList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const {
    getValues, getClearValues, control, setValue, reset,
  } = useFilterForm({}, loadData, ['policies_id']);
  const vid = getValues('policies_id');
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  React.useEffect(() => loadPolicies(getValues('policies_id')), [vid]);
  const DTO = new PoliciesDTO();
  const attributes = DTO.getAttributesByName();

  const handleSelectRow = item => {
    let select = item;
    if (selectedRow && selectedRow.policy_id === item.policy_id) {
      select = null;
    }
    setSelectedRow(select);
  };

  const handleCreate = () => {
    setIsEdit(false);
    setShowView(false);
    setShowForm(true);
    clearCurrentState();
  };

  const handleEdit = item => event => {
    if (!item) {
      return;
    }
    setValue('policies_id', item.policy_id);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDelete = item => event => {
    if (!item.policy_id) {
      errorNotification('Ошибка', 'Нет ID');
    }
    dispatch(showGlobalConfirm({
      confirmMessage: `Удаление политики # ${item.policy_id}`,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deletePolicies(item.policy_id)).then(() => {
        if (selectedRow === item.policy_id) {
          setSelectedRow(null);
        }
        loadData();
        successNotification('Удаление политики', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление политики', getResponseError(response));
      });
    }).catch(() => {});
    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  const copyLink = item => event => {
    const baseUrl = `${window.location.origin}${window.location.pathname}`;
    copyTextToClipboard(`${baseUrl}?policies_id=${item.policy_id}`).then(success => {
      if (success) {
        successNotification('Копирование ссылки', 'Ссылка скопирована');
      } else {
        errorNotification('Копирование ссылки', 'Не удалось скопировать ссылку');
      }
    });
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClose = () => {
    setShowForm(false);
    setValue('policies_id', '');
    clearCurrentState();
  };

  const clearCurrentState = () => {
    dispatch(loadPoliciesByIdClear()).catch(responseLogWithNotification);
  };

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={(
        <div className="more-wrapper">
          <Button variant="link" className="w-100 more-button" onClick={copyLink(item)}>
            Копировать ссылку
          </Button>
          <Button variant="link" className="w-100 more-button" onClick={handleEdit(item)}>
            Просмотреть
          </Button>
          <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}>
            Удалить
          </Button>
        </div>
      )}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  const rowClassFormatter = item => {
    const classes = ['clickable-table-row'];
    if (selectedRow && item.policy_id === selectedRow.policy_id) {
      classes.push('selected');
    }
    return classes.join(' ');
  };

  return (
    <div className="page application-page">
      <Loader loading={loadingPoliciesList || loadingCurrentPolicies || loadingDeletePolicies || loadingCreatePolicies || loadingEditPoliciesById} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.policies' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={RangeDatePicker}
            dtoObject={attributes.published_date}
            hideSmallButtons
            showArrowIcon
            usePortal
            placeholder="Дата публикации"
            changeFormat="YYYY-MM-DD"
            inputProps={{
              value: [getValues('published_date_from'), getValues('published_date_to')],
              onChange: e => {
                const v = e.target.value;
                setValue('published_date_from', v[0]);
                setValue('published_date_to', v[1]);
              },
            }}
            className="no-border"
          />
          <div className="filter-sep" />
          <Button variant="second" onClick={() => setOpenFilter(true)}>
            <Icons customIcon width={24} height={24} icon="filter" />
          </Button>
          <div className="filter-sep" />
          <Button variant="second" onClick={handleCreate}>
            <Icons
              customIcon
              width={24}
              height={24}
              icon="plus"
            />
          </Button>
          {isOpenFilter
            ? (
              <FilterForm
                control={control}
                onHide={() => {
                  setOpenFilter(false);
                }}
                getValues={getValues}
                reset={reset}
                defaultValues={{}}
                setValue={setValue}
              />
            )
            : null}
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                onClickByRow={handleSelectRow}
                onDoubleClickByRow={(item, event) => handleEdit(item)(event)}
                rowClassFormatter={rowClassFormatter}
                data={policiesList}
                columns={policiesColumns}
                additionalColumns={policiesAdditionalColumns}
                name="policies-list"
                settingsFormatter={moreFormatter}
              />
            </div>
          </div>
          <Pagination meta={policiesListMeta} list={policiesList} handleChangeMeta={loadData} />
        </div>
      </div>
      {showForm ? (
        <PoliciesForm
          currentPolicies={currentPolicies}
          isEdit={isEdit}
          loadData={loadData}
          onClose={handleClose}
          loadPolicies={loadPolicies}
        />
      ) : null}
    </div>
  );
}
